import './ApplicationAlert.less';

import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { AlertType } from '../../enums/alert';

/*
import { getLicense } from '../../services/subscription';
*/
import {
  SubscriptionStatus,
  SubscriptionData,
} from '../../interfaces/subscription';
import { RESTRICTED_SUBSCRIPTION_STATUSES } from '../../constants/subscription';
import {
  SUBSCRIPTION_EXPIRED_ALERT,
  SUBSCRIPTION_INCOMPLETE_ALERT,
  SUBSCRIPTION_INACTIVE_ALERT,
  SUBSCRIPTION_INVALID_ALERT,
  SUBSCRIPTION_NOT_FOUND_ALERT,
  getExpiringSoonMessage,
  getConvertingSoonMessage,
  getTrialEndingSoonMessage,
  getSubscriptionStatusMessage,
  getSFOrgStatusMessage,
  CONTACT_YOUR_ADMINISTRATOR,
} from '../../constants/texts';
import { AlertItem } from '../../interfaces/alert';
import { OrgStatus } from '../../interfaces/organization';
import { AppRoutes } from '../../constants/route';
import { useLocation } from 'react-router-dom';
import { useUserAccess } from '../../hooks/useUserAccess';
import { notification } from 'antd';

const _NOTIFICATION_ID = 'application-alert';

interface ApplicationAlertProps {
  restrictManageSubscription: boolean;
  handleSubscriptionClick: () => void;
  onAlertsChange: (alerts: AlertItem[]) => void;
}

const ApplicationAlert: React.FC<ApplicationAlertProps> = ({
  restrictManageSubscription,
  handleSubscriptionClick,
  onAlertsChange,
}) => {
  const location = useLocation();
  const [notificationApi, notificationContextHolder] =
    notification.useNotification();

  const { isTeamUser } = useUserAccess();
  const {
    subscriptionInfo,
    status: subscriptionStatus,
    loading: subscriptionLoading,
  } = useSelector((state: RootState) => state.subscription);
  const subscriptionData =
    subscriptionInfo?.subscription || ({} as SubscriptionData);
  const { org, loading: orgLoading } = useSelector(
    (state: RootState) => state.organization
  );
  const { isAccountSwitching } = useSelector((state: RootState) => state.team);

  // Reusable support contact blocks
  const supportLink = (
    <a
      href="https://docs.cirra.ai/mcp-server/support"
      target="_blank"
      rel="noopener noreferrer"
    >
      Cirra AI Support
    </a>
  );

  // Keep a stable reference to the latest onAlertsChange to avoid effect re-triggers
  const onAlertsChangeRef = useRef(onAlertsChange);
  useEffect(() => {
    onAlertsChangeRef.current = onAlertsChange;
  }, [onAlertsChange]);

  // Track the last alert id we notified the parent about
  const prevAlertIdRef = useRef<string | null>(null);

  const isSFOrgDependent = useMemo(() => {
    return Object.values(AppRoutes).some(
      (route) =>
        location.pathname.includes(route.path) && route.SF_ORG_dependent
    );
  }, [location.pathname]);

  useEffect(() => {
    // Don't show alerts while account switching or still loading
    if (isAccountSwitching || subscriptionLoading || orgLoading) {
      if (prevAlertIdRef.current !== null) {
        onAlertsChangeRef.current([]);
        prevAlertIdRef.current = null;
      }
      notificationApi.destroy(_NOTIFICATION_ID);
      return;
    }

    // Simple alert generation without Date.now() or complex logic
    let alertToShow: AlertItem | null = null;

    /*
    // Check for organization license first
    if (
      isTeamUser &&
      !getLicense() &&
      subscriptionStatus !== SubscriptionStatus.FREE &&
      !subscriptionData.freePlan
    ) {
      alertToShow = {
        id: 'no-license',
        type: AlertType.WARNING,
        message: (
          <>
            You don&apos;t have a license under your Organization account.
            Contact {supportLink}
          </>
        ),
        timestamp: 0,
      };
    }
    */

    // Check whether the trial has a payment method set up.
    // Without one, the trial will end (not convert to a paid plan).
    const noPaymentMethodSet = subscriptionData.rawData
      ? !subscriptionData.rawData.default_payment_method
      : !subscriptionData.trialEndBehavior ||
        subscriptionData.trialEndBehavior === 'cancel';

    // Check for trial expiration alerts first (more urgent).
    // Use convertsIn as fallback when expiresIn is absent but trial has no payment method,
    // since the trial will end (not convert) without one.
    const trialDaysLeft =
      subscriptionData.expiresIn ??
      (noPaymentMethodSet ? subscriptionData.convertsIn : undefined);
    if (
      trialDaysLeft !== undefined &&
      trialDaysLeft !== null &&
      trialDaysLeft <= 7 &&
      subscriptionStatus === SubscriptionStatus.TRIALING
    ) {
      alertToShow = {
        id: 'trial-expiring',
        type: AlertType.WARNING,
        message: (
          <>
            {getTrialEndingSoonMessage(trialDaysLeft)}{' '}
            {restrictManageSubscription ? (
              CONTACT_YOUR_ADMINISTRATOR
            ) : (
              <span
                onClick={handleSubscriptionClick}
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                Activate subscription
              </span>
            )}
          </>
        ),
        timestamp: 0,
      };
    }

    // Check for conversion alerts (only when trial has a payment method)
    if (
      !alertToShow &&
      subscriptionData.convertsIn !== undefined &&
      subscriptionData.convertsIn !== null &&
      subscriptionData.convertsIn <= 7 &&
      !noPaymentMethodSet
    ) {
      alertToShow = {
        id: 'trial-converting',
        type: AlertType.WARNING,
        message: (
          <>
            {getConvertingSoonMessage(
              subscriptionData.convertsIn,
              subscriptionData.convertingToPaidPlan || false,
              subscriptionData.conversionProduct || null
            )}{' '}
            {restrictManageSubscription ? (
              CONTACT_YOUR_ADMINISTRATOR
            ) : (
              <span
                onClick={handleSubscriptionClick}
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                Manage subscription
              </span>
            )}
          </>
        ),
        timestamp: 0,
      };
    }

    // Check for subscription expiring soon
    if (
      !alertToShow &&
      subscriptionData.expiresIn !== undefined &&
      subscriptionData.expiresIn !== null &&
      subscriptionData.expiresIn <= 7 &&
      subscriptionStatus === SubscriptionStatus.ACTIVE
    ) {
      alertToShow = {
        id: 'subscription-expiring',
        type: AlertType.WARNING,
        message: (
          <>
            {getExpiringSoonMessage(subscriptionData.expiresIn)}{' '}
            {restrictManageSubscription ? (
              CONTACT_YOUR_ADMINISTRATOR
            ) : (
              <span
                onClick={handleSubscriptionClick}
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                Renew subscription
              </span>
            )}
          </>
        ),
        timestamp: 0,
      };
    }

    // Check subscription status for restricted statuses
    if (!alertToShow && subscriptionStatus) {
      const status = subscriptionStatus.toLowerCase();
      if (
        RESTRICTED_SUBSCRIPTION_STATUSES.includes(status as SubscriptionStatus)
      ) {
        let message: React.ReactNode;

        switch (subscriptionStatus) {
          case SubscriptionStatus.INCOMPLETE:
            message = (
              <>
                {SUBSCRIPTION_INCOMPLETE_ALERT}{' '}
                {restrictManageSubscription ? (
                  CONTACT_YOUR_ADMINISTRATOR
                ) : (
                  <span
                    onClick={handleSubscriptionClick}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Complete setup
                  </span>
                )}
              </>
            );
            break;
          case SubscriptionStatus.INACTIVE:
            message = (
              <>
                {SUBSCRIPTION_INACTIVE_ALERT}{' '}
                {restrictManageSubscription ? (
                  CONTACT_YOUR_ADMINISTRATOR
                ) : (
                  <span
                    onClick={handleSubscriptionClick}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Reactivate subscription
                  </span>
                )}
              </>
            );
            break;
          case SubscriptionStatus.PAST_DUE:
            message = (
              <>
                {getSubscriptionStatusMessage(subscriptionStatus)}{' '}
                {restrictManageSubscription ? (
                  CONTACT_YOUR_ADMINISTRATOR
                ) : (
                  <span
                    onClick={handleSubscriptionClick}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Update payment
                  </span>
                )}
              </>
            );
            break;
          case SubscriptionStatus.PAUSED:
            message = (
              <>
                {getSubscriptionStatusMessage(subscriptionStatus)}{' '}
                {restrictManageSubscription ? (
                  CONTACT_YOUR_ADMINISTRATOR
                ) : (
                  <span
                    onClick={handleSubscriptionClick}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Resume subscription
                  </span>
                )}
              </>
            );
            break;
          case SubscriptionStatus.NOT_FOUND:
            message = (
              <>
                {SUBSCRIPTION_NOT_FOUND_ALERT}.{' '}
                {restrictManageSubscription ? (
                  CONTACT_YOUR_ADMINISTRATOR
                ) : (
                  <span
                    onClick={handleSubscriptionClick}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Set up subscription
                  </span>
                )}
              </>
            );
            break;
          case SubscriptionStatus.INVALID:
            message = (
              <>
                {SUBSCRIPTION_INVALID_ALERT}. Contact {supportLink}
              </>
            );
            break;
          default:
            message = (
              <>
                {SUBSCRIPTION_EXPIRED_ALERT}{' '}
                {restrictManageSubscription ? (
                  CONTACT_YOUR_ADMINISTRATOR
                ) : (
                  <span
                    onClick={handleSubscriptionClick}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Renew subscription
                  </span>
                )}
              </>
            );
        }

        alertToShow = {
          id: `subscription-${status}`,
          type: AlertType.ERROR,
          message,
          timestamp: 0,
        };
      }
    }

    // Check SF org status if relevant
    const isUsersPage = location.pathname.toLowerCase().includes('/users');
    const canShowSFOrgAlert =
      !isUsersPage &&
      ((!org && !orgLoading) || (org && org.status !== OrgStatus.ACTIVE)) &&
      isSFOrgDependent;

    if (canShowSFOrgAlert && !alertToShow) {
      alertToShow = {
        id: `sf-org-${org?.status}`,
        type: AlertType.WARNING,
        message: getSFOrgStatusMessage(org?.status),
        timestamp: 0,
      };
    }

    // Update parent only when the alert identity changes to avoid loops
    const currentAlertId = alertToShow?.id ?? null;
    if (currentAlertId !== prevAlertIdRef.current) {
      onAlertsChangeRef.current(alertToShow ? [alertToShow] : []);
      prevAlertIdRef.current = currentAlertId;
    }

    // Handle notifications directly here to avoid infinite loops
    if (alertToShow) {
      notificationApi.open({
        key: _NOTIFICATION_ID,
        message: alertToShow.message,
        type: alertToShow.type === AlertType.ERROR ? 'error' : 'warning',
        duration: 0,
        placement: 'top',
      });
    } else {
      notificationApi.destroy(_NOTIFICATION_ID);
    }
  }, [
    subscriptionStatus,
    subscriptionLoading,
    subscriptionInfo,
    org,
    orgLoading,
    location.pathname,
    isAccountSwitching,
    isTeamUser,
    isSFOrgDependent,
    restrictManageSubscription,
    handleSubscriptionClick,
  ]);

  return <div>{notificationContextHolder}</div>;
};

export default ApplicationAlert;
